import { yupResolver } from '@hookform/resolvers/yup'
import type { BaseSyntheticEvent } from 'react'
import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'

import { Button } from '@src/components/Button/Button'
import { ModalContent } from '@src/components/Modal/ModalContent'
import { TextField } from '@src/components/TextField/TextField'
import { Toast } from '@src/components/Toaster/Toast'
import type { NameSchema } from '@src/constants/AccountSchema'
import { nameSchema } from '@src/constants/AccountSchema'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { ServerActions } from '@src/services/server-actions.service'

interface Props {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  loading: boolean
  setLoading: (loading: boolean) => void
}

export function ChangeNameForm({ isOpen, setIsOpen, loading, setLoading }: Props) {
  const { t } = useLocale('accountPage')
  const { t: tcp } = useLocale('checkout-page')
  const { t: tcfe } = useLocale('checkout-form-errors')
  const { user, refreshToken } = useSession()

  const nameForm = useForm<NameSchema>({
    mode: 'onTouched',
    disabled: loading,
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
    },
    resolver: yupResolver(nameSchema),
  })

  useEffect(() => {
    if (isOpen) return
    nameForm.reset({ firstName: user?.firstName, lastName: user?.lastName })
  }, [nameForm, isOpen, user])

  const onNameFormSubmit = useCallback(
    async (data: NameSchema, event?: BaseSyntheticEvent) => {
      try {
        event?.preventDefault()
        if (loading) return
        setLoading(true)
        await ServerActions.serial.updateAccount(data)
        await refreshToken()
        toast(() => <Toast message={t('nameUpdated')} variant="success" />)
      } catch (error) {
        toast(() => <Toast message={t('error')} variant="error" />)
      } finally {
        setLoading(false)
        setIsOpen(false)
      }
    },
    [loading, refreshToken, setIsOpen, setLoading, t]
  )

  return (
    <ModalContent
      isOpen={isOpen}
      onClose={() => {
        if (loading) return
        setIsOpen(false)
      }}
      position="center"
      size="medium"
      title={t('editName')}
    >
      <form onSubmit={nameForm.handleSubmit(onNameFormSubmit)}>
        <div className="flex flex-col gap-4">
          <TextField
            id="firstName"
            data-testid="ship-first-name"
            labelText={tcp('first-name')}
            {...nameForm.register('firstName', { shouldUnregister: false })}
            captionText={tcfe(nameForm.formState.errors.firstName?.message)}
            isInvalid={!!nameForm.formState.errors.firstName?.message}
          />
          <TextField
            id="lastName"
            data-testid="ship-last-name"
            labelText={tcp('last-name')}
            {...nameForm.register('lastName', { shouldUnregister: false })}
            captionText={tcfe(nameForm.formState.errors.lastName?.message)}
            isInvalid={!!nameForm.formState.errors.lastName?.message}
          />
        </div>
        <div className="mt-6 flex flex-col gap-2 lg:mt-8 lg:flex-row">
          <Button isLoading={loading} size="large">
            {t('update')}
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault()
              setIsOpen(false)
            }}
            size="large"
            variant="secondary"
          >
            {t('cancel')}
          </Button>
        </div>
      </form>
    </ModalContent>
  )
}
